import React, { useEffect, useState, useMemo, useContext, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, getDoc, doc, onSnapshot, setDoc, query, limit, startAfter, orderBy, where } from "firebase/firestore";
import { db } from "../../firebase";
import { Card, CardContent, CardActions, Button, IconButton, Collapse, CircularProgress } from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import "./datatableClientes.scss";
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloseIcon from '@mui/icons-material/Close';
import StoreMallDirectoryRoundedIcon from '@mui/icons-material/StoreMallDirectoryRounded';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import datatableProfiler from './utils/DatatablePerformanceProfiler';

const DatatableClientes = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedClientInfo, setSelectedClientInfo] = useState(null);
  const [expandedContacts, setExpandedContacts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para lazy loading
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [totalLoaded, setTotalLoaded] = useState(0);
  
  // Estados para cache y throttling
  const [isUsingCache, setIsUsingCache] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Estados para búsqueda
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  
  // Referencias para evitar múltiples llamadas
  const isFetching = useRef(false);
  const fetchTimeout = useRef(null);
  const dataLoaded = useRef(false);
  const observerRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Constantes
  const ITEMS_PER_PAGE = 20; // Solo 20 elementos por carga
  const CACHE_KEY = 'datatable_clientes_cache';
  const CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutos
  const MAX_RETRIES = 3;
  const THROTTLE_DELAY = 2000; // 2 segundos entre intentos
  const SEARCH_DEBOUNCE_MS = 500; // 500ms para búsqueda

  const { currentUser, currentService, adminData } = useContext(AuthContext);

  // Función para obtener cache del localStorage
  const getCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp, serviceId, adminId } = JSON.parse(cached);
        const isValid = Date.now() - timestamp < CACHE_EXPIRY && 
                       serviceId === currentService?.id &&
                       adminId === adminData?.id;
        
        if (isValid && data && data.length > 0) {
          console.log('💾 Usando cache del localStorage:', data.length, 'clientes');
          return data;
        }
      }
    } catch (error) {
      console.warn('Error leyendo cache:', error);
    }
    return null;
  };

  // Función para guardar cache en localStorage
  const saveCache = (data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        serviceId: currentService?.id,
        adminId: adminData?.id
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      console.log('💾 Cache guardado en localStorage');
    } catch (error) {
      console.warn('Error guardando cache:', error);
    }
  };

  // Función para limpiar cache
  const clearCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY);
      console.log('🗑️ Cache limpiado');
    } catch (error) {
      console.warn('Error limpiando cache:', error);
    }
  };

  // Función para cargar clientes con lazy loading y búsqueda
  const fetchData = useCallback(async (useCache = true, reset = false, searchQuery = "") => {
    if (isFetching.current) {
      console.log('⏳ Ya se está cargando, esperando...');
      return;
    }

    if (!currentService?.id || !adminData?.id) {
      console.log('⏳ Faltan datos necesarios:', {
        currentServiceId: currentService?.id,
        adminDataId: adminData?.id
      });
      setError("Datos necesarios no disponibles. Verifique su sesión.");
      return;
    }

    // 📊 Iniciar sesión de profiling
    const sessionType = reset ? (searchQuery.trim() ? 'Busqueda' : 'CargaInicial') : 'LazyLoad';
    datatableProfiler.startSession(`DatatableClientes_${sessionType}`, {
      serviceId: currentService.id,
      adminDataId: adminData.id,
      useCache,
      reset,
      searchQuery: searchQuery || 'ninguna',
      itemsPerPage: ITEMS_PER_PAGE
    });

    // Solo usar cache si no hay búsqueda y es la primera carga
    if (useCache && reset && !searchQuery.trim()) {
      // 📊 Medir acceso a cache
      datatableProfiler.startMeasurement('11_CheckCache');

      const cachedData = getCache();

      datatableProfiler.endMeasurement('11_CheckCache', {
        cacheFound: !!cachedData,
        cachedItems: cachedData?.length || 0
      });

      if (cachedData) {
        setData(cachedData);
        setIsUsingCache(true);
        dataLoaded.current = true;
        setError(null);
        setTotalLoaded(cachedData.length);
        setHasMore(cachedData.length >= ITEMS_PER_PAGE);
        
        // Ocultar indicador de cache después de 3 segundos
        setTimeout(() => setIsUsingCache(false), 3000);

        // 📊 Finalizar sesión con cache
        datatableProfiler.endSession({
          desdeCache: true,
          clientesCargados: cachedData.length,
          success: true
        });

        return;
      }
    }

    isFetching.current = true;
    
    if (reset) {
      setLoading(true);
      setLastDoc(null);
    } else {
      setLoadingMore(true);
    }
    
    setError(null);

         try {
      const isSearch = searchQuery.trim().length > 0;
      console.log(`🔄 Cargando clientes desde Firebase... (reset: ${reset}, búsqueda: ${isSearch ? `"${searchQuery}"` : 'no'})`);

      // 📊 Medir la construcción de la query
      datatableProfiler.startMeasurement('12_BuildQuery', {
        isSearch,
        reset,
        hasLastDoc: !!lastDoc
      });
      
      const clientesRef = collection(db, "servicios", currentService.id, "clientes");
      
      let q;
      if (reset || !lastDoc) {
        // Primera carga o reset
        if (isSearch) {
           // BÚSQUEDA EN FIREBASE: Buscar en toda la colección (case-insensitive)
           // Los nombres en la BD están en mayúsculas, así que convertimos el término de búsqueda
           const searchTerm = searchQuery.trim().toUpperCase();
           q = query(
             clientesRef,
             where('nombre', '>=', searchTerm),
             where('nombre', '<=', searchTerm + '\uf8ff'),
             orderBy('nombre'),
             limit(ITEMS_PER_PAGE)
           );
           console.log('🔍 BÚSQUEDA EN FIREBASE por nombre (case-insensitive):', searchTerm);
         } else {
           // CARGA INICIAL: Solo los clientes asignados al admin
           if (adminData && adminData.clientes && adminData.clientes.length > 0) {
             // Obtener solo los clientes que están en adminData.clientes
             console.log('🔍 CARGA INICIAL: Obteniendo solo clientes asignados al admin');
             
             // Crear múltiples queries para obtener los clientes específicos
             const queries = adminData.clientes.slice(0, ITEMS_PER_PAGE).map(clienteId => 
               query(clientesRef, where('__name__', '==', clienteId))
             );
             
             // Ejecutar todas las queries
             const snapshots = await Promise.all(queries.map(q => getDocs(q)));
             const allDocs = snapshots.flatMap(snapshot => snapshot.docs);
             
             let newClientes = allDocs.map(doc => ({
               id: doc.id,
               ...doc.data()
             }));
             
             console.log('🔍 Clientes obtenidos directamente de adminData.clientes:', newClientes.length);
             
             // Actualizar estado
             if (reset) {
               setData(newClientes);
               setTotalLoaded(newClientes.length);
               console.log('✅ clientesData actualizado:', newClientes.length, 'clientes');
             } else {
               setData(prev => [...prev, ...newClientes]);
               setTotalLoaded(prev => prev + newClientes.length);
               console.log('✅ clientesData actualizado (append):', newClientes.length, 'clientes más');
             }
             
             // Actualizar paginación
             setHasMore(adminData.clientes.length > ITEMS_PER_PAGE);
             console.log('✅ Clientes cargados:', newClientes.length, 'hasMore:', adminData.clientes.length > ITEMS_PER_PAGE);
             
             return; // Salir de la función ya que manejamos todo aquí
           } else {
             // Si no hay clientes asignados, obtener los primeros 20
             q = query(
               clientesRef,
               orderBy('nombre'),
               limit(ITEMS_PER_PAGE)
             );
             console.log('📋 CARGA INICIAL: No hay clientes asignados, obteniendo primeros 20');
           }
         }
       } else {
         // Cargar más elementos
         if (isSearch) {
           // Continuar búsqueda (case-insensitive)
           const searchTerm = searchQuery.trim().toUpperCase();
           q = query(
             clientesRef,
             where('nombre', '>=', searchTerm),
             where('nombre', '<=', searchTerm + '\uf8ff'),
             orderBy('nombre'),
             startAfter(lastDoc),
             limit(ITEMS_PER_PAGE)
           );
           console.log('🔍 Query de búsqueda (paginación) - límite:', ITEMS_PER_PAGE);
         } else {
           // Continuar carga normal - obtener más clientes de adminData.clientes
           if (adminData && adminData.clientes && adminData.clientes.length > 0) {
             // Calcular cuántos clientes ya hemos cargado
             const alreadyLoaded = totalLoaded;
             const remainingClientes = adminData.clientes.slice(alreadyLoaded, alreadyLoaded + ITEMS_PER_PAGE);
             
             if (remainingClientes.length === 0) {
               console.log('📋 No hay más clientes para cargar');
               setHasMore(false);
               return;
             }
             
             console.log('🔍 Cargando más clientes de adminData.clientes:', {
               alreadyLoaded,
               remainingClientes: remainingClientes.length,
               totalAdminClientes: adminData.clientes.length
             });
             
             // Crear múltiples queries para obtener los clientes específicos
             const queries = remainingClientes.map(clienteId => 
               query(clientesRef, where('__name__', '==', clienteId))
             );
             
             // Ejecutar todas las queries
             const snapshots = await Promise.all(queries.map(q => getDocs(q)));
             const allDocs = snapshots.flatMap(snapshot => snapshot.docs);
             
             let newClientes = allDocs.map(doc => ({
               id: doc.id,
               ...doc.data()
             }));
             
             console.log('🔍 Clientes adicionales obtenidos:', newClientes.length);
             
             // Actualizar estado
             setData(prev => [...prev, ...newClientes]);
             setTotalLoaded(prev => prev + newClientes.length);
             
                                         // Actualizar paginación
              setHasMore(totalLoaded + newClientes.length < adminData.clientes.length);
              console.log('✅ Clientes adicionales cargados:', newClientes.length, 'hasMore:', totalLoaded + newClientes.length < adminData.clientes.length);
              
              // Si no hay más clientes para cargar, actualizar hasMore inmediatamente
              if (totalLoaded + newClientes.length >= adminData.clientes.length) {
                console.log('📋 Todos los clientes de adminData.clientes han sido cargados');
                setHasMore(false);
              }
              
              return; // Salir de la función ya que manejamos todo aquí
            } else {
             // Continuar carga normal
             q = query(
               clientesRef,
               orderBy('nombre'),
               startAfter(lastDoc),
               limit(ITEMS_PER_PAGE)
             );
             console.log('📥 Query de paginación creada - límite:', ITEMS_PER_PAGE);
           }
         }
       }

      datatableProfiler.endMeasurement('12_BuildQuery', {
        queryType: isSearch ? 'search' : (reset || !lastDoc) ? 'initial' : 'loadMore'
      });

      // 📊 Medir fetch desde Firestore
      datatableProfiler.startMeasurement('13_FetchFromFirestore', {
        isSearch,
        reset
      });

      const clientesSnapshot = await getDocs(q);

      datatableProfiler.endMeasurement('13_FetchFromFirestore', {
        docsReceived: clientesSnapshot.docs.length
      });

      // 📊 Medir procesamiento de datos
      datatableProfiler.startMeasurement('14_ProcessData', {
        docsCount: clientesSnapshot.docs.length
      });
      
      let newClientes = clientesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      datatableProfiler.endMeasurement('14_ProcessData', {
        clientesProcessed: newClientes.length
      });

      console.log(`✅ Clientes obtenidos: ${newClientes.length} (reset: ${reset}, búsqueda: ${isSearch})`);

      // 📊 Medir actualización de estado
      datatableProfiler.startMeasurement('15_UpdateState', {
        reset,
        isSearch
      });
      
      // Actualizar estado
      if (reset) {
        setData(newClientes);
        setTotalLoaded(newClientes.length);
        // Guardar en cache solo la primera carga sin búsqueda
        if (!isSearch && newClientes.length > 0) {
          // 📊 Medir guardado en cache
          datatableProfiler.startMeasurement('15a_SaveCache');
          saveCache(newClientes);
          datatableProfiler.endMeasurement('15a_SaveCache', {
            itemsSaved: newClientes.length
          });
        }
      } else {
        setData(prev => [...prev, ...newClientes]);
        setTotalLoaded(prev => prev + newClientes.length);
      }

      datatableProfiler.endMeasurement('15_UpdateState', {
        totalLoaded: reset ? newClientes.length : data.length + newClientes.length
      });
      
      // Actualizar paginación
      const lastVisibleDoc = clientesSnapshot.docs[clientesSnapshot.docs.length - 1];
      setLastDoc(lastVisibleDoc);
      setHasMore(clientesSnapshot.docs.length === ITEMS_PER_PAGE);
      
      dataLoaded.current = true;
      setRetryCount(0); // Reset retry count on success

      console.log(`📊 Estado actualizado - Total: ${reset ? newClientes.length : data.length + newClientes.length}, HasMore: ${clientesSnapshot.docs.length === ITEMS_PER_PAGE}`);

      // 📊 Finalizar sesión exitosa
      datatableProfiler.endSession({
        clientesCargados: newClientes.length,
        totalClientes: reset ? newClientes.length : data.length + newClientes.length,
        hasMore: clientesSnapshot.docs.length === ITEMS_PER_PAGE,
        isSearch: isSearch,
        success: true,
        desdeCache: false
      });

    } catch (error) {
      console.error("❌ Error al obtener los clientes:", error);
      
      // 📊 Finalizar sesión con error
      datatableProfiler.endSession({
        error: error.message || error.code || 'Unknown error',
        errorCode: error.code,
        success: false
      });
      
      // Manejar error específico de "Too many outstanding requests"
      if (error.code === 'resource-exhausted') {
        setError('Demasiadas peticiones simultáneas. Reintentando en unos segundos...');
        
        if (retryCount < MAX_RETRIES) {
          console.log(`🔄 Reintentando en ${THROTTLE_DELAY/1000} segundos... (intento ${retryCount + 1}/${MAX_RETRIES})`);
          
          setRetryCount(prev => prev + 1);
          
          // Programar reintento con throttling
          fetchTimeout.current = setTimeout(() => {
            fetchData(false, reset, searchQuery); // No usar cache en reintentos
          }, THROTTLE_DELAY);
        } else {
          setError('Error de conexión. Por favor, recarga la página.');
          console.error('Máximo de reintentos alcanzado');
        }
      } else if (error.message && error.message.includes('Cannot read properties of null')) {
        setError('Error de sincronización de datos. Recargando automáticamente...');
        console.log('🔄 Error de sincronización, reintentando en 2 segundos...');
        
        setTimeout(() => {
          if (adminData) {
            fetchData(false, reset, searchQuery);
          }
        }, 2000);
      } else {
        setError('Error al cargar los clientes. Por favor, recarga la página.');
      }
    } finally {
      isFetching.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentService, adminData, getCache, saveCache]);

           // Función para cargar más elementos
    const loadMore = useCallback(async () => {
      if (!hasMore || loadingMore || isFetching.current || !adminData) return;
      
      // Verificar si ya hemos cargado todos los clientes de adminData.clientes
      if (adminData.clientes && adminData.clientes.length > 0) {
        const alreadyLoaded = totalLoaded;
        const remainingClientes = adminData.clientes.slice(alreadyLoaded, alreadyLoaded + ITEMS_PER_PAGE);
        
        if (remainingClientes.length === 0) {
          console.log('📋 No hay más clientes para cargar - todos ya cargados');
          setHasMore(false);
          return;
        }
      }
      
      console.log('📜 Cargando más clientes...');
      await fetchData(false, false, searchDebounce);
    }, [hasMore, loadingMore, adminData, totalLoaded, searchDebounce]);

  // Debounce para búsqueda
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Ejecutar búsqueda cuando cambie el término
  useEffect(() => {
    if (searchDebounce.trim() !== filter.trim()) {
      console.log('🔄 Ejecutando búsqueda:', searchDebounce);
      setFilter(searchDebounce);
      setIsSearching(true);
      
      // Mantener el foco en el input de búsqueda
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      
      // Resetear datos y cargar con búsqueda
      setData([]);
      setTotalLoaded(0);
      setLastDoc(null);
      setHasMore(true);
      dataLoaded.current = false;
      
      if (searchDebounce.trim()) {
        fetchData(false, true, searchDebounce);
      } else {
        // Si no hay término de búsqueda, cargar datos normales
        fetchData(true, true, "");
      }
      
      // Mover setIsSearching(false) al final de fetchData para evitar parpadeos
      setTimeout(() => setIsSearching(false), 100);
    }
  }, [searchDebounce, filter, fetchData]);

  // Cargar datos iniciales
  useEffect(() => {
    if (currentService?.id && adminData && !dataLoaded.current) {
      console.log('🔄 Iniciando carga de datos...');
      fetchData(true, true, "");
    }
  }, [currentService, adminData]);

  // Recargar datos cuando adminData se cargue
  useEffect(() => {
    if (currentService?.id && adminData && dataLoaded.current && data.length === 0) {
      console.log('🔄 adminData cargado, recargando datos...');
      dataLoaded.current = false;
      fetchData(true, true, "");
    }
  }, [adminData, currentService, data.length]);

           // Observer para scroll infinito
    useEffect(() => {
      if (!hasMore || loadingMore || data.length === 0 || !adminData) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loadingMore && !isFetching.current) {
            console.log('📜 Observer activado - cargando más clientes...');
            
            // Verificar si ya hemos cargado todos los clientes de adminData.clientes
            if (adminData.clientes && adminData.clientes.length > 0) {
              const alreadyLoaded = totalLoaded;
              const remainingClientes = adminData.clientes.slice(alreadyLoaded, alreadyLoaded + ITEMS_PER_PAGE);
              
              if (remainingClientes.length === 0) {
                console.log('📋 No hay más clientes para cargar - todos ya cargados');
                setHasMore(false);
                return;
              }
            }
            
            loadMore();
          }
        },
        { threshold: 0.1 }
      );

      if (observerRef.current) {
        observer.observe(observerRef.current);
      }

      return () => {
        if (observerRef.current) {
          observer.unobserve(observerRef.current);
        }
      };
    }, [hasMore, loadingMore, data.length, totalLoaded, adminData, loadMore]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, []);

  const handleDelete = async (id) => {
    try {
      const confirmDelete = window.confirm(
        "¿Estás seguro de que deseas eliminar este cliente?"
      );
      if (confirmDelete) {
        // Encuentra y actualiza el campo "borrado" en lugar de eliminar el cliente
        const serviciosSnapshot = await getDocs(collection(db, "servicios"));

        for (const servicioDoc of serviciosSnapshot.docs) {
          const clienteRef = doc(servicioDoc.ref, "clientes", id);

          // Verifica si el elemento ya está marcado como borrado
          const clienteDoc = await getDoc(clienteRef);
          if (clienteDoc.exists() && clienteDoc.data().borrado) {
            // Recupera el elemento si ya está borrado
            await setDoc(clienteRef, { borrado: false }, { merge: true });
          } else {
            // Marca como borrado si no está borrado
            await setDoc(clienteRef, { borrado: true }, { merge: true });
          }
        }

        // Actualiza la lista local estableciendo "borrado: true" o mueve el elemento a la lista de recuperados
        setData((prevData) =>
          prevData.map((item) =>
            item.id === id
              ? item.borrado
                ? { ...item, borrado: false }
                : { ...item, borrado: true }
              : item
          )
        );

        // Limpiar cache para forzar recarga
        clearCache();
      }
    } catch (err) {
      console.error("Error al eliminar cliente:", err);
    }
  };

  const filteredData = useMemo(() => {
    // Restaurar filtrado por borrado
    const filtered = data.filter((row) => {
      return showDeleted ? row.borrado : !row.borrado;
    });
    
    return filtered;
  }, [data, showDeleted]);

  const handleMailClick = (cliente) => {
    if (cliente.email) {
      setSelectedClientInfo((prevInfo) => ({
        ...prevInfo,
        [cliente.id]: `Correo electrónico: ${cliente.email}`,
      }));
    }
  };

  const handlePhoneClick = (cliente) => {
    if (cliente.telefono) {
      setSelectedClientInfo((prevInfo) => ({
        ...prevInfo,
        [cliente.id]: `Número de teléfono: ${cliente.telefono}`,
      }));
    }
  };

  const clearSelectedClientInfo = (id) => {
    setSelectedClientInfo((prevInfo) => {
      const newInfo = { ...prevInfo };
      delete newInfo[id];
      return newInfo;
    });
  };

  const toggleContacts = (clienteId) => {
    setExpandedContacts(prev => ({
      ...prev,
      [clienteId]: !prev[clienteId]
    }));
  };

  const renderContactInfo = (contacts) => {
    if (!contacts || contacts.length === 0) return null;
    
    return contacts.map((contact, index) => (
      <div key={index} className="contactInfo">
        <div className="contactHeader">
          <PersonIcon className="contactIcon" />
          <span className="contactName">{contact.nombre}</span>
        </div>
        <div className="contactEmail">{contact.email}</div>
      </div>
    ));
  };

  const renderClienteCard = (cliente) => {
    if (showDeleted || !cliente.borrado) {
      return (
        <Card key={cliente.id} className="clienteCard">
          <CardContent>
            <div className="cardHeader">
              {cliente.img ? (
                <img src={cliente.img} alt="Logo" className="clienteLogo" />
              ) : (
                <StoreMallDirectoryRoundedIcon className="clienteLogo" style={{ color: '#4678c3', fontSize: 40 }} />
              )}
              <div className="clienteInfo">
                <div className="nombreServicio">{cliente.nombre}</div>
                <div className="contactoLogos">
                  <IconButton className="iconContainer" onClick={() => handleMailClick(cliente)}>
                    <MailOutlineIcon className="whiteIcon" />
                  </IconButton>
                  <IconButton size="small" className="iconContainer" onClick={() => handlePhoneClick(cliente)}>
                    <PhoneEnabledIcon className="whiteIcon" />
                  </IconButton>
                  {cliente.contacts && cliente.contacts.length > 0 && (
                    <IconButton 
                      size="small" 
                      className="iconContainer"
                      onClick={() => toggleContacts(cliente.id)}
                    >
                      {expandedContacts[cliente.id] ? 
                        <ExpandLessIcon className="whiteIcon" /> : 
                        <ExpandMoreIcon className="whiteIcon" />
                      }
                    </IconButton>
                  )}
                </div>
              </div>
            </div>
            
            {cliente.id && selectedClientInfo && selectedClientInfo[cliente.id] && (
              <div className="selectedClientInfo">
                {selectedClientInfo[cliente.id]}
                <IconButton size="small" onClick={() => clearSelectedClientInfo(cliente.id)}>
                  <CloseIcon className="closeIcon" />
                </IconButton>
              </div>
            )}
            
            <Collapse in={expandedContacts[cliente.id]} timeout="auto" unmountOnExit>
              <div className="contactsContainer">
                {renderContactInfo(cliente.contacts)}
              </div>
            </Collapse>

            <div className="idServicio">Provincia: {cliente?.provincia}</div>
            <div className="idServicio">Comunidad: {cliente?.comunidad}</div>
          </CardContent>
          <hr />
          <CardActions className="acciones">
            <Link to={`/clientes/${encodeURIComponent(cliente.id)}/tiendas/${encodeURIComponent(cliente.nombre)}`}>
              <Button size="small" className="verButton">
                Ver
              </Button>
            </Link>
            <div className="rightActions">
              {!cliente.borrado && (
                <Link to={`/clientes/update/${encodeURIComponent(cliente.id)}`}>
                  <IconButton size="small">
                    <EditIcon className="editarIcon" />
                  </IconButton>
                </Link>
              )}
              {cliente.borrado ?
                <Button size="small" className="recuperarButton" onClick={() => handleDelete(cliente.id)}>
                  Recuperar
                </Button>
                : (
                  <IconButton size="small" color="secondary" onClick={() => handleDelete(cliente.id)}>
                    <DeleteIcon className="borrarIcon" />
                  </IconButton>
                )}
            </div>
          </CardActions>
        </Card>
      );
    }
  };

  /**
   * Maneja el cambio en la casilla de verificación "Mostrar borrados".
   * @param {object} event - Evento de cambio.
   */
  const handleShowDeletedChange = (event) => {
    setShowDeleted(event.target.checked);
  };

  // Función para limpiar búsqueda
  const handleClearSearch = () => {
    setSearchTerm('');
    setFilter('');
    setData([]);
    setTotalLoaded(0);
    setLastDoc(null);
    setHasMore(true);
    dataLoaded.current = false;
    fetchData(true, true, "");
  };

  // Función para recargar datos manualmente
  const handleReload = () => {
    clearCache();
    dataLoaded.current = false;
    setRetryCount(0);
    setData([]);
    setTotalLoaded(0);
    setHasMore(true);
    setSearchTerm('');
    setFilter('');
    fetchData(false, true, "");
  };

  // Mostrar loading inicial
  if (loading && data.length === 0) {
    return (
      <div className="datatable">
        <div className="datatableTitle">
          <h2 className="title-registro">Registro Clientes</h2>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
          <span style={{ marginLeft: '16px' }}>
            {retryCount > 0 ? `Reintentando... (intento ${retryCount}/${MAX_RETRIES})` : 'Cargando clientes...'}
          </span>
        </div>
      </div>
    );
  }

  // Mostrar loading cuando no hay adminData
  if (!adminData && currentService) {
    return (
      <div className="datatable">
        <div className="datatableTitle">
          <h2 className="title-registro">Registro Clientes</h2>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
          <span style={{ marginLeft: '16px' }}>
            Cargando datos de administrador...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="datatable">
      <div className="datatableTitle">
        <h2 className="title-registro">Registro Clientes</h2>
        <div className="rightActions">
          <FormControlLabel
            control={
              <Checkbox
                checked={showDeleted}
                onChange={handleShowDeletedChange}
                color="primary"
              />
            }
            label="Mostrar borrados"
          />
          <div className="searchContainer">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar por nombre..."
              className="searchInput"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <SearchIcon className="searchIcon" />
            {searchTerm && (
              <IconButton size="small" onClick={handleClearSearch} style={{ marginLeft: '8px' }}>
                <CloseIcon />
              </IconButton>
            )}
          </div>
          <Link to="/clientes/newCliente" className="link">
            Nuevo cliente
          </Link>
        </div>
      </div>

      {/* Indicador de cache */}
      {isUsingCache && (
        <div style={{ 
          padding: '8px 16px', 
          backgroundColor: '#e3f2fd', 
          color: '#1976d2', 
          borderRadius: '4px',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          ⚡ Datos cargados desde caché - Carga instantánea
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div style={{ 
          padding: '12px 16px', 
          backgroundColor: '#ffebee', 
          color: '#c62828', 
          borderRadius: '4px',
          marginBottom: '16px',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>⚠️ {error}</span>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={handleReload}
            style={{ color: '#c62828', borderColor: '#c62828' }}
          >
            Reintentar
          </Button>
        </div>
      )}

      {/* Información de resultados */}
      <div style={{ 
        padding: '8px 16px', 
        backgroundColor: '#f5f5f5', 
        borderRadius: '4px',
        marginBottom: '16px',
        fontSize: '14px',
        color: '#666'
      }}>
        {searchTerm ? 
          `${filteredData.length} clientes encontrados • ${totalLoaded} cargados de la BD • Búsqueda: "${searchTerm}"`
          : `${filteredData.length} clientes mostrados • ${totalLoaded} cargados de la BD`
        }
        {hasMore && !loadingMore && (
          <span style={{ marginLeft: '16px', color: '#1976d2' }}>
            • Haz scroll para cargar más (20 por vez)
          </span>
        )}
        {data.length > 0 && !searchTerm && (
          <span style={{ marginLeft: '16px', color: '#666' }}>
            • Cache válido por 10 minutos
          </span>
        )}
      </div>

      <div className="cardsContainer">
        {filteredData.map(renderClienteCard)}
        
        {/* Loading indicator para más elementos */}
        {loadingMore && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '32px',
            width: '100%'
          }}>
            <CircularProgress size={24} />
            <span style={{ marginLeft: '12px' }}>Cargando más clientes...</span>
          </div>
        )}
        
        {/* Observer target para infinite scroll */}
        {hasMore && !loadingMore && (
          <div ref={observerRef} style={{ height: '20px' }} />
        )}
        
        {/* Mensaje de fin */}
        {!hasMore && data.length > 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '24px', 
            color: '#666',
            fontSize: '14px',
            borderTop: '1px solid #eee',
            marginTop: '16px'
          }}>
            No hay más clientes que mostrar
          </div>
        )}
        
        {/* Mensaje cuando no hay datos */}
        {filteredData.length === 0 && !loading && !error && (
          <div style={{ 
            textAlign: 'center', 
            padding: '48px', 
            color: '#666',
            fontSize: '16px'
          }}>
            {searchTerm ? 
              `No se encontraron clientes que coincidan con "${searchTerm}"`
              : "No hay clientes disponibles"
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default DatatableClientes;